import {
  type ComponentProps,
  type ReactNode,
  type RefObject,
  useState,
} from "react";
import {
  AssetsIcon,
  AudienceIcon,
  Box,
  Button,
  CanvasIcon,
  Column,
  EmptyState,
  FileIcon,
  FrameStackIcon,
  GlobeIcon,
  ImageIcon,
  MailIcon,
  PaperclipIcon,
  SyncIcon,
  useToast,
} from "@hightouch/htui";
import { formatDistanceToNow } from "date-fns";
import { useMeasure } from "react-use";
import { EMAIL_PICKER_EXCLUDED_SOURCES } from "@hightouch/lib/llm/shared";

import {
  ASSET_CARD_ASPECT_RATIO,
  AssetCardFooter,
  AssetGrid,
  AssetCardMedia,
  LoadMoreFooter,
  AssetPickerFooter,
  AssetPickerModal,
  AssetSearchPane,
  MissingAssetPlaceholder,
  SelectableAssetCard,
  type AssetPaneData,
  type AssetSource,
} from "src/components/asset-picker";
import {
  AttachableTableSkeleton,
  type AudienceOption,
  AudienceTable,
  type SyncOption,
  SyncTable,
} from "src/pages/projects/components/attachable-tables";
import { EmailPreview } from "src/components/email-preview";
import {
  AmpFileType,
  OrderBy,
  useAdBriefAssetsListQuery,
  useAmpAttachableAudiencesQuery,
  useAmpAttachableLibraryCountsQuery,
  useAmpAttachableSyncsQuery,
  useContentEmailsQuery,
  type AdBriefAssetsListQuery,
  type AmpLibraryFileFragment,
  type ContentEmailsQuery,
} from "src/graphql";
import {
  AdAssetPreview,
  getAdAssetKind,
} from "src/pages/ad-studio/components/ad-asset-preview";
import { adDisplayName } from "src/pages/ad-studio/components/ad-display-name";
import { DEFAULT_AD_ASSETS_ORDER_BY } from "src/pages/ad-studio/hooks/use-ad-assets-infinite";
import {
  NOT_PRIVATE_FILTER,
  useCanSeePrivateMessages,
} from "src/pages/lifecycle-studio/messages/use-private-message-visibility";
import { VISIBLE_AD_BRIEF_ASSET_FILTER } from "src/pages/ad-studio/user-facing-project-filter";
import { FileThumbnail } from "src/pages/projects/library/file-card";
import { useFilePages } from "src/pages/projects/library/use-file-pages";
import { toIlikePattern } from "src/utils/ilike-pattern";

// Stays under listAmpFiles' limit cap of 100.
const PAGE_SIZE = 24;
export type AddContentType =
  | "library"
  | "canvas"
  | "ad"
  | "email"
  | "html"
  | "doc"
  | "slides"
  | "file"
  | "audience"
  | "sync";
/** The panes that list Library files, which they pick by file id. "file" lists uploads already in the Library. */
type LibraryFileType =
  | "library"
  | "canvas"
  | "html"
  | "doc"
  | "slides"
  | "file";
/** What the picker hands back. A Library file comes back by its file id. */
export type ContentSelection<T extends AddContentType = AddContentType> = {
  type: T;
  id: string;
};
type AdOption = AdBriefAssetsListQuery["ad_brief_assets"][number];
type EmailOption = ContentEmailsQuery["content_emails"][number];
/** A picked item, with what its card or row needs to show it again in the Selected pane. */
type Picked =
  | { type: "ad"; id: string; ad: AdOption }
  | { type: "email"; id: string; email: EmailOption }
  | { type: LibraryFileType; id: string; file: AmpLibraryFileFragment }
  | { type: "audience"; id: string; audience: AudienceOption }
  | { type: "sync"; id: string; sync: SyncOption };
type Props<T extends AddContentType> = {
  /** The modal title. */
  title: string;
  /** The panes the picker offers, in rail order. With "library", the other Library panes sit under it. */
  types: readonly [T, ...T[]];
  /** The pane it opens on. Defaults to the first of `types`. */
  initialType?: T;
  /** Lets the person pick several items, across panes, and adds each one. */
  multiple?: boolean;
  /** The confirm button's label for this many items. Defaults to the title. */
  confirmLabel?: (count: number) => string;
  /** Where focus goes when the picker closes. Defaults to the element that opened it. */
  finalFocusRef?: RefObject<HTMLElement>;
  onClose: () => void;
  /** Adds one picked item; called once for each, in order, with its index among the items this confirm adds. A rejection keeps the picker open, with the items not yet added, and shows the error. */
  onConfirm: (
    selection: ContentSelection<T>,
    batch: { index: number; selections: ContentSelection<T>[] },
  ) => Promise<void>;
};

const NOUNS: Record<AddContentType, string> = {
  library: "file",
  canvas: "canvas",
  ad: "ad",
  email: "email",
  html: "web page",
  doc: "doc",
  slides: "presentation",
  file: "file",
  audience: "audience",
  sync: "sync",
};

const LIBRARY_FILE_TYPES = new Set<AddContentType>([
  "library",
  "canvas",
  "html",
  "doc",
  "slides",
  "file",
]);

// The Library's panes, in the order they sit under it.
const LIBRARY_CHILDREN: AddContentType[] = [
  "canvas",
  "ad",
  "email",
  "html",
  "doc",
  "slides",
  "file",
];

/** One key per picked thing: a Library file is the same pick from whichever pane it was picked in. */
function pickKey(type: AddContentType, id: string): string {
  return LIBRARY_FILE_TYPES.has(type) ? `file:${id}` : `${type}:${id}`;
}

export function AddContentPicker<T extends AddContentType>({
  title,
  types,
  initialType,
  multiple = false,
  confirmLabel = () => title,
  finalFocusRef,
  onClose,
  onConfirm,
}: Props<T>) {
  const [view, setView] = useState<string>(initialType ?? types[0]);
  const [picked, setPicked] = useState<Picked[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  // The panes open on these same queries, so the rail counts share their cache.
  const ads = useAdOptions("");
  const emails = useEmailOptions("");
  // A pane the picker doesn't offer has no count to fetch.
  const offers = (type: AddContentType) =>
    (types as readonly AddContentType[]).includes(type);
  const audiences = useAudienceOptions("", offers("audience"));
  const syncs = useSyncOptions("", offers("sync"));
  const libraryCounts = useAmpAttachableLibraryCountsQuery(undefined, {
    enabled: [...LIBRARY_FILE_TYPES].some(offers),
  });
  const libraryCount = (
    key: "all" | "canvas" | "html" | "doc" | "upload",
  ): Pick<AssetSource, "count" | "isLoadingCount"> => ({
    count: libraryCounts.data?.[key].aggregate?.count,
    isLoadingCount: libraryCounts.isLoading,
  });
  const { toast } = useToast();
  // Only the panes in `types` render, so each selection made here is one of T.
  const add = (
    selection: ContentSelection,
    batch: { index: number; selections: ContentSelection[] },
  ) =>
    onConfirm(selection as ContentSelection<T>, {
      index: batch.index,
      selections: batch.selections as ContentSelection<T>[],
    });
  const count = picked.length;
  const close = () => {
    if (!isAdding) onClose();
  };
  const isSelected = (type: AddContentType, id: string) =>
    picked.some((item) => pickKey(item.type, item.id) === pickKey(type, id));
  // The selection holds still while it is being added.
  const toggle = (item: Picked) => {
    if (isAdding) return;
    setPicked((current) =>
      isSelected(item.type, item.id)
        ? current.filter(
            (other) =>
              pickKey(other.type, other.id) !== pickKey(item.type, item.id),
          )
        : multiple
          ? [...current, item]
          : [item],
    );
  };
  /** In a multi-select picker, a pane's button that selects every item it shows, or deselects them once all are selected. */
  const selectAllAction = <I,>(toPicked: (item: I) => Picked) =>
    multiple
      ? ({ items, hasMore }: { items: I[]; hasMore: boolean }) => {
          const shown = items.map(toPicked);
          const keys = new Set(
            shown.map((item) => pickKey(item.type, item.id)),
          );
          const isAllSelected = shown.every((item) =>
            isSelected(item.type, item.id),
          );
          // Only the loaded page can be picked, so the label says so while more remain.
          const scope = hasMore ? `${shown.length} shown` : "all";
          return (
            <Button
              size="sm"
              variant="tertiary"
              isDisabled={isAdding}
              onClick={() =>
                setPicked((current) =>
                  isAllSelected
                    ? current.filter(
                        (item) => !keys.has(pickKey(item.type, item.id)),
                      )
                    : [
                        ...current,
                        ...shown.filter(
                          (item) => !isSelected(item.type, item.id),
                        ),
                      ],
                )
              }
            >
              {isAllSelected ? `Deselect ${scope}` : `Select ${scope}`}
            </Button>
          );
        }
      : undefined;
  /** A table pane's header checkbox: the shown rows leave the selection, and the ones that end up selected join it. */
  const setShownPicked = <I,>(
    shown: I[],
    selected: I[],
    toPicked: (item: I) => Picked,
  ) => {
    if (isAdding) return;
    const shownKeys = new Set(
      shown.map((item) => {
        const { type, id } = toPicked(item);
        return pickKey(type, id);
      }),
    );
    setPicked((current) => [
      ...current.filter((item) => !shownKeys.has(pickKey(item.type, item.id))),
      ...selected.map(toPicked),
    ]);
  };
  const selectedIds = (type: "audience" | "sync") =>
    picked.flatMap((item) => (item.type === type ? [item.id] : []));
  const adPick = (ad: AdOption): Picked => ({ type: "ad", id: ad.id, ad });
  const emailPick = (email: EmailOption): Picked => ({
    type: "email",
    id: email.id,
    email,
  });
  const filePick =
    (type: LibraryFileType) =>
    (file: AmpLibraryFileFragment): Picked => ({ type, id: file.id, file });
  const audiencePick = (audience: AudienceOption): Picked => ({
    type: "audience",
    id: String(audience.id),
    audience,
  });
  const syncPick = (sync: SyncOption): Picked => ({
    type: "sync",
    id: String(sync.id),
    sync,
  });
  // The pane and the Selected view show an item the same way, and a click in either toggles it.
  const adCard = (ad: AdOption) => (
    <AdCard
      key={ad.id}
      ad={ad}
      name={adDisplayName({
        assetName: ad.asset_name,
        summary: ad.ad_brief?.summary,
      })}
      selected={isSelected("ad", ad.id)}
      onSelect={() => toggle(adPick(ad))}
    />
  );
  const emailCard = (email: EmailOption) => (
    <EmailCard
      key={email.id}
      email={email}
      name={email.name || "Untitled email"}
      selected={isSelected("email", email.id)}
      onSelect={() => toggle(emailPick(email))}
    />
  );
  const fileCard = (type: LibraryFileType) =>
    function renderFileCard(file: AmpLibraryFileFragment) {
      return (
        <LibraryFileCard
          key={file.id}
          file={file}
          name={file.name || "Untitled"}
          selected={isSelected(type, file.id)}
          onSelect={() => toggle(filePick(type)(file))}
        />
      );
    };
  const confirm = async () => {
    if (count === 0 || isAdding) return;
    setIsAdding(true);
    // Named so a failure says which kind of item it was.
    let current: AddContentType = "ad";
    try {
      // Each added item leaves the selection, so a retry after a failure adds only the rest.
      const selections = picked.map(({ type, id }) => ({ type, id }));
      for (const [index, item] of picked.entries()) {
        current = item.type;
        await add({ type: item.type, id: item.id }, { index, selections });
        setPicked((items) => items.filter((other) => other !== item));
      }
      onClose();
    } catch (error) {
      setIsAdding(false);
      toast({
        id: "add-content",
        variant: "error",
        title: `Could not add ${NOUNS[current]}`,
        message: error instanceof Error ? error.message : "Try again.",
      });
    }
  };
  const sources: Record<AddContentType, AssetSource> = {
    ad: {
      id: "ad",
      label: "Ads",
      icon: ImageIcon,
      count: ads.totalCount,
      isLoadingCount: ads.isLoading,
      pane: (
        <AssetSearchPane
          label="ad"
          pluralLabel="ads"
          searchPlaceholder="Search ads…"
          useData={useAdOptions}
          headerActions={selectAllAction(adPick)}
          emptyState={(hasSearch) =>
            hasSearch
              ? { title: "No matching ads", message: "Try another search." }
              : {
                  title: "No ads yet",
                  message: "Ads you make in Ad Studio show up here.",
                }
          }
          results={{ layout: "grid", renderItem: adCard }}
        />
      ),
    },
    email: {
      id: "email",
      label: "Emails",
      icon: MailIcon,
      count: emails.totalCount,
      isLoadingCount: emails.isLoading,
      pane: (
        <AssetSearchPane
          label="email"
          pluralLabel="emails"
          searchPlaceholder="Search emails…"
          useData={useEmailOptions}
          headerActions={selectAllAction(emailPick)}
          emptyState={(hasSearch) =>
            hasSearch
              ? { title: "No matching emails", message: "Try another search." }
              : {
                  title: "No emails yet",
                  message: "Emails you make in Lifecycle Studio show up here.",
                }
          }
          results={{ layout: "grid", renderItem: emailCard }}
        />
      ),
    },
    library: {
      id: "library",
      label: "Library",
      icon: AssetsIcon,
      ...libraryCount("all"),
      pane: libraryFilePane({
        label: "file",
        pluralLabel: "files",
        useData: useAllFileOptions,
        renderItem: fileCard("library"),
        headerActions: selectAllAction(filePick("library")),
      }),
    },
    canvas: {
      id: "canvas",
      label: "Canvases",
      icon: CanvasIcon,
      ...libraryCount("canvas"),
      pane: libraryFilePane({
        label: "canvas",
        pluralLabel: "canvases",
        useData: useCanvasOptions,
        renderItem: fileCard("canvas"),
        headerActions: selectAllAction(filePick("canvas")),
      }),
    },
    html: {
      id: "html",
      label: "Web pages",
      icon: GlobeIcon,
      ...libraryCount("html"),
      pane: libraryFilePane({
        label: "web page",
        pluralLabel: "web pages",
        useData: useWebPageOptions,
        renderItem: fileCard("html"),
        headerActions: selectAllAction(filePick("html")),
      }),
    },
    doc: {
      id: "doc",
      label: "Docs",
      icon: FileIcon,
      ...libraryCount("doc"),
      pane: libraryFilePane({
        label: "doc",
        pluralLabel: "docs",
        useData: useDocOptions,
        renderItem: fileCard("doc"),
        headerActions: selectAllAction(filePick("doc")),
      }),
    },
    slides: {
      id: "slides",
      label: "Slides",
      icon: FrameStackIcon,
      pane: libraryFilePane({
        label: "presentation",
        pluralLabel: "presentations",
        useData: useSlidesOptions,
        renderItem: fileCard("slides"),
        headerActions: selectAllAction(filePick("slides")),
      }),
    },
    file: {
      id: "file",
      label: "Files",
      icon: PaperclipIcon,
      ...libraryCount("upload"),
      pane: libraryFilePane({
        label: "file",
        pluralLabel: "files",
        useData: useUploadedFileOptions,
        renderItem: fileCard("file"),
        headerActions: selectAllAction(filePick("file")),
      }),
    },
    audience: {
      id: "audience",
      label: "Audiences",
      icon: AudienceIcon,
      count: audiences.totalCount,
      isLoadingCount: audiences.isLoading,
      pane: (
        <AssetSearchPane
          label="audience"
          pluralLabel="audiences"
          searchPlaceholder="Search audiences…"
          useData={useAudienceOptions}
          emptyState={(hasSearch) =>
            hasSearch
              ? {
                  title: "No matching audiences",
                  message: "Try another search.",
                }
              : {
                  title: "No audiences yet",
                  message: "Audiences you build show up here.",
                }
          }
          renderSkeleton={() => <AttachableTableSkeleton kind="audience" />}
          results={{
            layout: "custom",
            render: ({ items, hasMore, isLoadingMore, loadMore }) => (
              <>
                <AudienceTable
                  rows={items}
                  rowLabel={audienceLabel}
                  selectedIds={selectedIds("audience")}
                  isDisabled={isAdding}
                  onToggle={(audience) => toggle(audiencePick(audience))}
                  onSelectShown={(selected) =>
                    setShownPicked(items, selected, audiencePick)
                  }
                />
                <LoadMoreFooter
                  shownCount={items.length}
                  hasMore={hasMore}
                  isLoadingMore={isLoadingMore}
                  onLoadMore={loadMore}
                  label="audiences"
                />
              </>
            ),
          }}
        />
      ),
    },
    sync: {
      id: "sync",
      label: "Syncs",
      icon: SyncIcon,
      count: syncs.totalCount,
      isLoadingCount: syncs.isLoading,
      pane: (
        <AssetSearchPane
          label="sync"
          pluralLabel="syncs"
          searchPlaceholder="Search syncs…"
          useData={useSyncOptions}
          emptyState={(hasSearch) =>
            hasSearch
              ? { title: "No matching syncs", message: "Try another search." }
              : {
                  title: "No syncs yet",
                  message: "Syncs you set up show up here.",
                }
          }
          renderSkeleton={() => <AttachableTableSkeleton kind="sync" />}
          results={{
            layout: "custom",
            render: ({ items, hasMore, isLoadingMore, loadMore }) => (
              <>
                <SyncTable
                  rows={items}
                  rowLabel={syncLabel}
                  selectedIds={selectedIds("sync")}
                  isDisabled={isAdding}
                  onToggle={(sync) => toggle(syncPick(sync))}
                  onSelectShown={(selected) =>
                    setShownPicked(items, selected, syncPick)
                  }
                />
                <LoadMoreFooter
                  shownCount={items.length}
                  hasMore={hasMore}
                  isLoadingMore={isLoadingMore}
                  onLoadMore={loadMore}
                  label="syncs"
                />
              </>
            ),
          }}
        />
      ),
    },
  };
  // With a Library pane, the Library's other panes sit under it on the rail.
  const isInLibrary = (type: AddContentType) =>
    offers("library") && LIBRARY_CHILDREN.includes(type);
  return (
    <AssetPickerModal
      isOpen
      finalFocusRef={finalFocusRef}
      variant={multiple ? "multi" : "single"}
      title={title}
      onClose={close}
      activeViewId={view}
      onActiveViewChange={setView}
      sources={types
        .filter((type) => !isInLibrary(type))
        .map((type) =>
          type === "library"
            ? {
                ...sources.library,
                children: LIBRARY_CHILDREN.filter(offers).map(
                  (child) => sources[child],
                ),
              }
            : sources[type],
        )}
      sourcesLabel="Select from"
      selectedCount={count}
      selectedPane={
        multiple ? (
          <SelectedPane
            cards={picked.flatMap((item) =>
              item.type === "ad"
                ? [adCard(item.ad)]
                : item.type === "email"
                  ? [emailCard(item.email)]
                  : "file" in item
                    ? [fileCard(item.type)(item.file)]
                    : [],
            )}
            audiences={picked.flatMap((item) =>
              item.type === "audience" ? [item.audience] : [],
            )}
            syncs={picked.flatMap((item) =>
              item.type === "sync" ? [item.sync] : [],
            )}
            onToggleAudience={(audience) => toggle(audiencePick(audience))}
            onToggleSync={(sync) => toggle(syncPick(sync))}
            isDisabled={isAdding}
          />
        ) : undefined
      }
      footer={
        <AssetPickerFooter
          onCancel={close}
          onConfirm={() => void confirm()}
          confirmLabel={confirmLabel(count)}
          isConfirmDisabled={count === 0}
          isConfirmLoading={isAdding}
          isCancelDisabled={isAdding}
        />
      }
    />
  );
}

const audienceLabel = (audience: AudienceOption) => audience.name;
const syncLabel = (sync: SyncOption) => sync.segment?.name ?? `Sync ${sync.id}`;

/** Everything the confirm will add, shown as each pane shows it. Unchecking an item drops it. */
function SelectedPane({
  cards,
  audiences,
  syncs,
  onToggleAudience,
  onToggleSync,
  isDisabled,
}: {
  /** Ads, emails and Library files, as their cards. */
  cards: ReactNode[];
  audiences: AudienceOption[];
  syncs: SyncOption[];
  onToggleAudience: (audience: AudienceOption) => void;
  onToggleSync: (sync: SyncOption) => void;
  isDisabled: boolean;
}) {
  if (cards.length === 0 && audiences.length === 0 && syncs.length === 0) {
    return (
      <EmptyState
        title="Nothing selected"
        message="Pick items from any source. Everything you select shows up here and gets added together."
      />
    );
  }
  return (
    <Column height="100%" minHeight={0} overflowY="auto" gap={4}>
      {cards.length > 0 && <AssetGrid>{cards}</AssetGrid>}
      {audiences.length > 0 && (
        <AudienceTable
          rows={audiences}
          rowLabel={audienceLabel}
          selectedIds={audiences.map((audience) => String(audience.id))}
          isDisabled={isDisabled}
          onToggle={onToggleAudience}
          onSelectShown={(selected) => {
            for (const audience of audiences) {
              if (!selected.includes(audience)) onToggleAudience(audience);
            }
          }}
        />
      )}
      {syncs.length > 0 && (
        <SyncTable
          rows={syncs}
          rowLabel={syncLabel}
          selectedIds={syncs.map((sync) => String(sync.id))}
          isDisabled={isDisabled}
          onToggle={onToggleSync}
          onSelectShown={(selected) => {
            for (const sync of syncs) {
              if (!selected.includes(sync)) onToggleSync(sync);
            }
          }}
        />
      )}
    </Column>
  );
}

/** A pane's Library files, of one type or all. They page by offset and report no total. */
function useLibraryFileOptions(
  type: AmpFileType | undefined,
  search: string,
): AssetPaneData<AmpLibraryFileFragment> {
  const query = useFilePages({
    type,
    search: search || undefined,
    limit: PAGE_SIZE,
  });
  return {
    items: query.files,
    isLoading: query.isLoading,
    isRefreshing: query.isFetching,
    isLoadingMore: query.isFetchingNextPage,
    hasMore: Boolean(query.hasNextPage),
    loadMore: () => void query.fetchNextPage(),
    error: query.error ? "Could not load. Try again." : null,
  };
}

function useAllFileOptions(search: string) {
  return useLibraryFileOptions(undefined, search);
}

function useCanvasOptions(search: string) {
  return useLibraryFileOptions(AmpFileType.Canvas, search);
}

function useWebPageOptions(search: string) {
  return useLibraryFileOptions(AmpFileType.Html, search);
}

function useDocOptions(search: string) {
  return useLibraryFileOptions(AmpFileType.Doc, search);
}

function useSlidesOptions(search: string) {
  return useLibraryFileOptions(AmpFileType.Slides, search);
}

function useUploadedFileOptions(search: string) {
  return useLibraryFileOptions(AmpFileType.Upload, search);
}

/** A searchable grid of Library files. */
function libraryFilePane({
  label,
  pluralLabel,
  useData,
  renderItem,
  headerActions,
}: Pick<
  ComponentProps<typeof AssetSearchPane<AmpLibraryFileFragment>>,
  "label" | "pluralLabel" | "useData" | "headerActions"
> & {
  renderItem: (file: AmpLibraryFileFragment) => ReactNode;
}) {
  return (
    <AssetSearchPane
      label={label}
      pluralLabel={pluralLabel}
      searchPlaceholder={`Search ${pluralLabel}…`}
      useData={useData}
      emptyState={(hasSearch) =>
        hasSearch
          ? {
              title: `No matching ${pluralLabel}`,
              message: "Try another search.",
            }
          : {
              title: `No ${pluralLabel} yet`,
              message: `${pluralLabel[0]?.toUpperCase()}${pluralLabel.slice(1)} in the Library show up here.`,
            }
      }
      results={{ layout: "grid", renderItem }}
      headerActions={headerActions}
    />
  );
}

/** Grows the fetched rows a page at a time, starting over when the search changes. */
function usePagedLimit(search: string) {
  const [paged, setPaged] = useState({ search, limit: PAGE_SIZE });
  const limit = paged.search === search ? paged.limit : PAGE_SIZE;
  return {
    limit,
    loadMore: () => setPaged({ search, limit: limit + PAGE_SIZE }),
  };
}

function useAdOptions(search: string): AssetPaneData<AdOption> {
  const { limit, loadMore } = usePagedLimit(search);
  const query = useAdBriefAssetsListQuery(
    {
      where: search
        ? {
            _and: [
              VISIBLE_AD_BRIEF_ASSET_FILTER,
              {
                _or: [
                  { asset_name: { _ilike: toIlikePattern(search) } },
                  { ad_brief: { summary: { _ilike: toIlikePattern(search) } } },
                ],
              },
            ],
          }
        : VISIBLE_AD_BRIEF_ASSET_FILTER,
      orderBy: DEFAULT_AD_ASSETS_ORDER_BY,
      limit,
    },
    { keepPreviousData: true },
  );
  return pageData(
    query,
    query.data?.ad_brief_assets ?? [],
    query.data?.ad_brief_assets_aggregate.aggregate?.count ?? 0,
    limit,
    loadMore,
  );
}

function useEmailOptions(search: string): AssetPaneData<EmailOption> {
  const { limit, loadMore } = usePagedLimit(search);
  const canSeePrivateMessages = useCanSeePrivateMessages();
  const pattern = toIlikePattern(search);
  const query = useContentEmailsQuery(
    {
      filters: {
        source: { _nin: EMAIL_PICKER_EXCLUDED_SOURCES },
        ...(canSeePrivateMessages ? {} : NOT_PRIVATE_FILTER),
        ...(search
          ? {
              _or: [
                { name: { _ilike: pattern } },
                { subject: { _ilike: pattern } },
              ],
            }
          : {}),
      },
      orderBy: [{ updated_at: OrderBy.Desc }],
      limit,
    },
    { keepPreviousData: true },
  );
  return pageData(
    query,
    query.data?.content_emails ?? [],
    query.data?.content_emails_aggregate.aggregate?.count ?? 0,
    limit,
    loadMore,
  );
}

function useAudienceOptions(
  search: string,
  enabled = true,
): AssetPaneData<AudienceOption> {
  const { limit, loadMore } = usePagedLimit(search);
  // The audiences the Audiences page lists.
  const query = useAmpAttachableAudiencesQuery(
    {
      where: {
        query_type: { _eq: "visual" },
        _not: { campaigns: {} },
        ...(search ? { name: { _ilike: toIlikePattern(search) } } : {}),
      },
      limit,
    },
    { keepPreviousData: true, enabled },
  );
  return pageData(
    query,
    query.data?.segments ?? [],
    query.data?.segments_aggregate.aggregate?.count ?? 0,
    limit,
    loadMore,
  );
}

function useSyncOptions(
  search: string,
  enabled = true,
): AssetPaneData<SyncOption> {
  const { limit, loadMore } = usePagedLimit(search);
  const pattern = toIlikePattern(search);
  const query = useAmpAttachableSyncsQuery(
    {
      where: search
        ? {
            _or: [
              { segment: { name: { _ilike: pattern } } },
              { destination: { name: { _ilike: pattern } } },
            ],
          }
        : {},
      limit,
    },
    { keepPreviousData: true, enabled },
  );
  return pageData(
    query,
    query.data?.destination_instances ?? [],
    query.data?.destination_instances_aggregate.aggregate?.count ?? 0,
    limit,
    loadMore,
  );
}

function pageData<T>(
  query: {
    isLoading: boolean;
    isFetching: boolean;
    isPreviousData: boolean;
    error: unknown;
  },
  items: T[],
  totalCount: number,
  limit: number,
  loadMore: () => void,
): AssetPaneData<T> {
  return {
    items,
    totalCount,
    isLoading: query.isLoading,
    isRefreshing: query.isFetching,
    isLoadingMore: query.isFetching && query.isPreviousData,
    hasMore: items.length >= limit && items.length < totalCount,
    loadMore,
    error: query.error ? "Could not load. Try again." : null,
  };
}

function updatedLabel(updatedAt: string | null | undefined) {
  return updatedAt
    ? `Updated ${formatDistanceToNow(new Date(updatedAt), { addSuffix: true })}`
    : undefined;
}

type CardProps = {
  name: string;
  selected: boolean;
  onSelect: () => void;
};

function AdCard({
  ad,
  name,
  selected,
  onSelect,
}: CardProps & { ad: AdOption }) {
  return (
    <Box sx={{ aspectRatio: ASSET_CARD_ASPECT_RATIO }}>
      <SelectableAssetCard
        selected={selected}
        checkboxLabel={name}
        onToggleSelect={onSelect}
        onClick={onSelect}
        footer={
          <AssetCardFooter
            title={name}
            subtitle={updatedLabel(ad.updated_at)}
          />
        }
      >
        <AssetCardMedia>
          {getAdAssetKind(ad) === "empty" ? (
            <MissingAssetPlaceholder label="No preview" />
          ) : (
            <AdAssetPreview asset={ad} variant="thumbnail" />
          )}
        </AssetCardMedia>
      </SelectableAssetCard>
    </Box>
  );
}

function EmailCard({
  email,
  name,
  selected,
  onSelect,
}: CardProps & { email: EmailOption }) {
  const [mediaRef, { width, height }] = useMeasure<HTMLDivElement>();
  const html = email.content_email_version?.resolved_html?.html;
  return (
    <Box sx={{ aspectRatio: ASSET_CARD_ASPECT_RATIO }}>
      <SelectableAssetCard
        selected={selected}
        checkboxLabel={name}
        onToggleSelect={onSelect}
        onClick={onSelect}
        footer={
          <AssetCardFooter
            title={name}
            subtitle={
              email.content_email_version?.subject ??
              email.subject ??
              updatedLabel(email.updated_at)
            }
          />
        }
      >
        <AssetCardMedia>
          <Box ref={mediaRef} width="100%" height="100%">
            {!html ? (
              <MissingAssetPlaceholder label="No preview" />
            ) : width > 0 && height > 0 ? (
              <EmailPreview html={html} width={width} height={height} />
            ) : null}
          </Box>
        </AssetCardMedia>
      </SelectableAssetCard>
    </Box>
  );
}

/** Any Library file, previewed as the Library previews it. */
function LibraryFileCard({
  file,
  name,
  selected,
  onSelect,
}: CardProps & { file: AmpLibraryFileFragment }) {
  return (
    // Some previews are a fixed width, which would otherwise widen their grid column.
    <Box minWidth={0} sx={{ aspectRatio: ASSET_CARD_ASPECT_RATIO }}>
      <SelectableAssetCard
        selected={selected}
        checkboxLabel={name}
        onToggleSelect={onSelect}
        onClick={onSelect}
        footer={
          <AssetCardFooter
            title={name}
            subtitle={updatedLabel(file.updatedAt)}
          />
        }
      >
        <AssetCardMedia>
          <FileThumbnail file={file} />
        </AssetCardMedia>
      </SelectableAssetCard>
    </Box>
  );
}
